import React, { useState } from "react";
import {
  Drawer,
  Button,
  Typography,
  Space,
  Spin,
  Popconfirm,
  Descriptions,
  Tag,
  Alert,
} from "antd";
import {
  CopyOutlined,
  SyncOutlined,
  DeleteOutlined,
  PlusOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { toast } from "react-toastify";
import { tagStyles } from "./ApplicationsTable";

const { Text } = Typography;

export default function ApplicationSecretsDrawer({
  visible,
  onClose,
  application,
  loading,
  generatePrimarySecret,
  rotateSecret,
  revokeSecondarySecret,
}) {
  const [processing, setProcessing] = useState(false);
  const [primarySecret, setPrimarySecret] = useState(null);
  const [showSecretWarning, setShowSecretWarning] = useState(false);

  const maskSecret = (secret) => {
    if (!secret) return "";
    const last4 = secret.slice(-4);
    return `******${last4}`;
  };

  const handleGeneratePrimary = async () => {
    try {
      setProcessing(true);
      const secret = await generatePrimarySecret(application.id);
      setPrimarySecret(secret);
      application.hasPrimarySecret = true;
      setShowSecretWarning(true);
    } catch (err) {
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const handleRotate = async () => {
    try {
      setProcessing(true);
      const newSecret = await rotateSecret(application.id);
      setPrimarySecret(newSecret);
      application.hasPrimarySecret = true;
      application.hasSecondarySecret = true;
      setShowSecretWarning(true);
    } catch (err) {
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const handleRevokeSecondary = async () => {
    try {
      setProcessing(true);
      await revokeSecondarySecret(application.id);
      application.hasSecondarySecret = false;
    } catch (err) {
      console.error(err);
    } finally {
      setProcessing(false);
    }
  };

  const handleClose = () => {
    setShowSecretWarning(false);
    setPrimarySecret(null);
    onClose();
  };

  return (
    <Drawer
      title={
        <span style={{ fontFamily: "Inter", fontSize: 15 }}>
          Secrets for {application?.name}
        </span>
      }
      size={500}
      zIndex={99999}
      onClose={handleClose}
      open={visible}
      footer={
        <div style={{ textAlign: "right" }}>
          <Button onClick={handleClose}>Close</Button>
        </div>
      }
    >
      <Spin spinning={loading || processing}>
        {application && (
          <>
            <Descriptions
              size="small"
              column={1}
              bordered
              style={{ marginBottom: 16, fontFamily: "Inter" }}
            >
              <Descriptions.Item
                label="Application Name"
                style={{ fontSize: 12 }}
              >
                {application.name}
              </Descriptions.Item>
              <Descriptions.Item label="Code" style={{ fontSize: 12 }}>
                {application.code || "-"}
              </Descriptions.Item>

              <Descriptions.Item label="Status" style={{ fontSize: 12 }}>
                <Tag color={application.active ? "green" : "red"} style={tagStyles}>
                  {application.active ? "Active" : "Inactive"}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Client ID" style={{ fontSize: 12 }}>
                <Text
                  copyable={{
                    text: application.clientId,
                    tooltips: ["Copy Client ID", "Copied!"],
                  }}
                  style={{ fontFamily: "monospace" }}
                >
                  {application.clientId}
                </Text>
              </Descriptions.Item>

              <Descriptions.Item
                label="Has Primary Secret"
                style={{ fontSize: 12 }}
              >
                {application.hasPrimarySecret ? "Yes" : "No"}
              </Descriptions.Item>

              <Descriptions.Item
                label="Has Secondary Secret"
                style={{ fontSize: 12 }}
              >
                {application.hasSecondarySecret ? "Yes" : "No"}
              </Descriptions.Item>
            </Descriptions>
          </>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            marginBottom: 20,
          }}
        >
          {/* primary secret */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
                fontFamily: "Inter",
                fontSize: 13,
              }}
            >
              <strong>Primary Secret:</strong>
              {/* <Space>
                {application?.hasPrimarySecret || primarySecret ? (
                  <>
                    <Text
                      copyable={{
                        text: primarySecret || "********",
                        tooltips: ["Copy secret", "Copied!"],
                      }}
                      style={{ fontFamily: "monospace" }}
                    >
                      {primarySecret ? maskSecret(primarySecret) : "********"}
                    </Text>
                    <Button
                      size="small"
                      icon={<SyncOutlined />}
                      onClick={handleRotate}
                    >
                      Rotate
                    </Button>
                  </>
                ) : (
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleGeneratePrimary}
                  >
                    Generate
                  </Button>
                )}
              </Space> */}
              <Space>
                {application?.hasPrimarySecret || primarySecret ? (
                  <>
                    {primarySecret ? (
                      <Text
                        copyable={{
                          text: primarySecret,
                          tooltips: ["Copy secret", "Copied!"],
                        }}
                        style={{ fontFamily: "monospace" }}
                      >
                        {maskSecret(primarySecret)}
                      </Text>
                    ) : (
                      <Text style={{ fontFamily: "monospace" }}>
                        ******{application.primarySecretLast4 || ""}
                      </Text>
                    )}
                    <Button
                      size="small"
                      icon={<SyncOutlined />}
                      onClick={handleRotate}
                    >
                      Rotate
                    </Button>
                  </>
                ) : (
                  <Button
                    size="small"
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleGeneratePrimary}
                  >
                    Generate
                  </Button>
                )}
              </Space>
            </div>
          </div>

          {/* secondary secret */}
          <div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginBottom: 4,
                fontFamily: "Inter",
                fontSize: 13,
              }}
            >
              <strong>Secondary Secret:</strong>
              {/* <Space>
                {application?.hasSecondarySecret ? (
                  <>
                    <Text
                      copyable={{
                        text: "********",
                        tooltips: ["Copy secret", "Copied!"],
                      }}
                      style={{ fontFamily: "monospace" }}
                    >
                      ********
                    </Text>
                    <Popconfirm
                      title="Revoke secondary secret?"
                      onConfirm={handleRevokeSecondary}
                    >
                      <Button size="small" icon={<DeleteOutlined />}>
                        Revoke
                      </Button>
                    </Popconfirm>
                  </>
                ) : (
                  <span style={{ color: "#888" }}>Not created</span>
                )}
              </Space> */}
              <Space>
                {application?.hasSecondarySecret ? (
                  <>
                    <Text style={{ fontFamily: "monospace" }}>
                      ******{application.secondarySecretLast4 || ""}
                    </Text>
                    <Popconfirm
                      title="Revoke secondary secret?"
                      onConfirm={handleRevokeSecondary}
                    >
                      <Button size="small" icon={<DeleteOutlined />}>
                        Revoke
                      </Button>
                    </Popconfirm>
                  </>
                ) : (
                  <span style={{ color: "#888" }}>Not created</span>
                )}
              </Space>
            </div>
          </div>
        </div>

        {showSecretWarning && (
          <Alert
            title="Copy the new primary secret now! It will be hidden after this."
            type="warning"
            showIcon
            icon={<ExclamationCircleOutlined />}
            style={{ fontFamily: "Inter", fontSize: 12 }}
          />
        )}
      </Spin>
    </Drawer>
  );
}
